// ---------------------------------------------------------------------------
// DeleteParticipantDataDialog — Purge all data for a participant.
//
// Hard-deletes every MeetingData entry containing the given participant (by
// email or alias) across all versions of a dataset. This is an irreversible,
// owner-level maintenance action backed by
// DELETE /datasets/{dataSetId}/participants/data?emailOrAlias=.
// ---------------------------------------------------------------------------

import {
  Body1,
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  Spinner,
} from '@fluentui/react-components';
import { useCallback, useEffect, useState } from 'react';

import type { DeleteParticipantDataResult } from '../../../services/dataHubService.interface.js';
import { withCorrelationId } from '../../../services/usageContext.js';
import { useScenarioFactory } from '../../../telemetry/hooks/useScenarioFactory/useScenarioFactory.js';
import { classifyScenarioError } from '../../../telemetry/scenarioError.js';
import { ScenarioName } from '../../../telemetry/scenarios.js';

import type { DeleteParticipantDataDialogProps } from './DeleteParticipantDataDialog.interface.js';
import { useStyles } from './DeleteParticipantDataDialog.style.js';

export function DeleteParticipantDataDialog({
  open,
  dataSetId,
  datasetName,
  service,
  initialEmailOrAlias,
  onClose,
  onDeleted,
}: DeleteParticipantDataDialogProps) {
  const styles = useStyles();
  const scenarioFactory = useScenarioFactory();

  const [emailOrAlias, setEmailOrAlias] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<DeleteParticipantDataResult | null>(
    null,
  );

  useEffect(() => {
    if (open) {
      setEmailOrAlias(initialEmailOrAlias ?? '');
      setIsDeleting(false);
      setError(null);
      setResult(null);
    }
  }, [open, initialEmailOrAlias]);

  const trimmed = emailOrAlias.trim();
  const canSubmit = trimmed !== '' && !isDeleting;

  const handleConfirm = useCallback(async () => {
    if (trimmed === '') return;
    setIsDeleting(true);
    setError(null);
    const scenario = scenarioFactory.newScenario(
      ScenarioName.DatalakeDeleteParticipantData,
    );
    try {
      const deleteResult = await service.hardDeleteByParticipant(
        dataSetId,
        trimmed,
        {
          extras: withCorrelationId(scenario.queryId),
        },
      );
      scenario.complete('success');
      setResult(deleteResult);
      onDeleted();
    } catch (err) {
      const classification = classifyScenarioError(err);
      scenario.complete(classification.status, classification.data);
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to delete participant data',
      );
    } finally {
      setIsDeleting(false);
    }
  }, [service, dataSetId, trimmed, onDeleted, scenarioFactory]);

  return (
    <Dialog
      open={open}
      onOpenChange={(_, data) => {
        if (!data.open && !isDeleting) onClose();
      }}
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Delete participant data?</DialogTitle>
          <DialogContent>
            {result !== null ? (
              <Body1 className={styles.result}>
                Deleted {result.deletedCount}{' '}
                {result.deletedCount === 1 ? 'entry' : 'entries'} for &ldquo;
                {result.emailOrAlias ?? trimmed}&rdquo;.
              </Body1>
            ) : (
              <div className={styles.form}>
                <Body1 className={styles.warning}>
                  All data containing this participant in &ldquo;{datasetName}
                  &rdquo; will be permanently deleted across all versions
                  (Draft, Published, Versioned). This action cannot be undone.
                </Body1>
                <Field
                  label="Participant email or alias"
                  required
                  validationState={error !== null ? 'error' : 'none'}
                  validationMessage={error ?? undefined}
                >
                  <Input
                    value={emailOrAlias}
                    onChange={(_, data) => setEmailOrAlias(data.value)}
                    placeholder="user@contoso.com"
                    disabled={isDeleting}
                  />
                </Field>
              </div>
            )}
          </DialogContent>
          <DialogActions>
            {result !== null ? (
              <Button appearance="primary" onClick={onClose}>
                Close
              </Button>
            ) : (
              <>
                <Button
                  appearance="secondary"
                  disabled={isDeleting}
                  onClick={onClose}
                >
                  Cancel
                </Button>
                <Button
                  data-testid="delete-participant-confirm-button"
                  appearance="primary"
                  className={styles.confirmButton}
                  disabled={!canSubmit}
                  icon={isDeleting ? <Spinner size="tiny" /> : undefined}
                  onClick={() => {
                    void handleConfirm();
                  }}
                >
                  Delete
                </Button>
              </>
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
